import type { PaginatedPassengerAddressList } from '@bambi/api-client/schemas';
import { useEffect } from 'react';
import type { UseFormSetValue } from 'react-hook-form';

import type { AddTripFormValues, Trip } from 'src/features/add-trip/FormValues';

interface useSetPickupAddressProps {
  recentAddressResponse: PaginatedPassengerAddressList | undefined;
  index: number;
  setValue: UseFormSetValue<AddTripFormValues>;
  trips: Trip[];
  enabled: boolean;
}

/**
 * Sets the pickup address when the recent address response is available.
 * @param recentAddressResponse - The recent address response.
 * @param index - The index of the trip in the trips array.
 * @param setValue - The setValue function.
 * @param trips - The trips array.
 * @param enabled - Whether to enable setting the pickup address for this field.
 * @returns void
 */
export default function useSetPickupAddress({
  recentAddressResponse,
  index,
  setValue,
  trips,
  enabled,
}: useSetPickupAddressProps) {
  useEffect(() => {
    if (enabled && recentAddressResponse?.results.length) {
      const primaryAddress = recentAddressResponse.results.find(
        (result) => result.is_primary_address
      );

      const currentTrip = trips[index];
      if (
        primaryAddress &&
        // Don't override an existing pickup address
        !currentTrip.pickupLocation.address.value &&
        // Assume they don't want to go in a circle
        currentTrip.dropoffLocation.address.value !==
          primaryAddress.address.location?.place_id
      ) {
        setValue(`trips.${index}.pickupLocation.address`, {
          label: primaryAddress.address.location?.label ?? '',
          value: primaryAddress.address.location?.place_id ?? '',
        });
        setValue(
          `trips.${index}.pickupLocation.address_details`,
          primaryAddress.address_details
        );
        setValue(
          `trips.${index}.pickupLocation.driver_notes`,
          primaryAddress.address_notes
        );
      }
    }
  }, [enabled, recentAddressResponse, trips, index, setValue]);
}
